import { useState } from 'react';
import SelectedEmplacementDeliveryInput from '../../components/SelectedEmplacementDeliveryInput';
import SelectedModelInput from '../../components/SelectedModelInput';
import { EXTRA_OPTIONS, FUEL_OPTIONS, KM_OPTIONS } from '../../constants/vehicleOptions';

const FIRST_YEAR = 1990;
const YEARS = Array.from({ length: new Date().getFullYear() - FIRST_YEAR + 1 }, (_, i) =>
  String(new Date().getFullYear() - i)
);
const range = (a, b) => Array.from({ length: b - a + 1 }, (_, i) => a + i);

// Même composant que dans AdvancedConfigPage
function Choice({ type = 'radio', variant = '', id, name, value, checked, onChange, children }) {
  return (
    <div className={`mc-choice ${variant}`.trim()}>
      <input type={type} id={id} name={name} value={value} checked={checked} onChange={onChange} />
      <label htmlFor={id}>{children}</label>
    </div>
  );
}

function StepQuestions({ state, patch, busy, onPlace, onBack, onNext }) {
  const [editing, setEditing] = useState(!state.modele);
  const vehicle = state.analysis?.vehicle;
  const candidates = vehicle?.modelCandidates || [];
  const lowConfidence = vehicle && (vehicle.confidence ?? 1) < 0.8;
  const fuelLabel = FUEL_OPTIONS.find(([v]) => v === state.carburantType)?.[1];

  const toggleOption = (id) =>
    patch((s) => ({
      options: s.options.includes(id) ? s.options.filter((o) => o !== id) : [...s.options, id],
    }));

  const canContinue = Boolean(
    state.modele && state.place && state.kilometrage && state.vitesseType
  );

  return (
    <section className="mc-form-card">
      <span className="mc-section-kicker">VOTRE VOITURE</span>
      <h1 className="mc-form-title">Vérifiez et répondez à 3 questions</h1>

      {/* Ce que l'IA a lu */}
      <div className="mc-field">
        <div className="mv-express-summary">
          <strong>
            {state.modele ? `${state.modele.marque} ${state.modele.modele}` : 'Modèle à choisir'}
          </strong>
          <span>
            {state.annee} · {fuelLabel} · {state.places} places
          </span>
          <button
            type="button"
            className="mc-btn mc-btn--ghost"
            onClick={() => setEditing((v) => !v)}
          >
            {editing ? 'Fermer' : 'Modifier'}
          </button>
        </div>
        {lowConfidence && (
          <p className="mc-field-hint">Lecture peu certaine : vérifiez ces informations.</p>
        )}

        {candidates.length > 1 && !state.modele && (
          <div className="mv-express-chips">
            {candidates.map((c) => (
              <button
                key={c.id}
                type="button"
                className="mv-express-chip"
                onClick={() => patch({ modele: c })}
              >
                {c.marque} {c.modele}
              </button>
            ))}
          </div>
        )}

        {editing && (
          <div className="mv-express-edit">
            <SelectedModelInput
              resetInput={false}
              handleSelectModel={(m) =>
                patch({ modele: { id: m.id, marque: m.marque, modele: m.modele } })
              }
            />
            <div className="mc-grid-2 mv-express-fields">
              <select
                className="mc-select"
                value={state.annee}
                onChange={(e) => patch({ annee: e.target.value })}
                aria-label="Année"
              >
                {!YEARS.includes(state.annee) && <option value={state.annee}>{state.annee}</option>}
                {YEARS.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
              <select
                className="mc-select"
                value={state.carburantType}
                onChange={(e) => patch({ carburantType: e.target.value })}
                aria-label="Carburant"
              >
                {FUEL_OPTIONS.map(([v, label]) => (
                  <option key={v} value={v}>
                    {label}
                  </option>
                ))}
              </select>
              <select
                className="mc-select"
                value={state.places}
                onChange={(e) => patch({ places: Number(e.target.value) })}
                aria-label="Places"
              >
                {range(2, 9).map((n) => (
                  <option key={n} value={n}>
                    {n} places
                  </option>
                ))}
              </select>
              <select
                className="mc-select"
                value={state.portes}
                onChange={(e) => patch({ portes: Number(e.target.value) })}
                aria-label="Portes"
              >
                {range(2, 7).map((n) => (
                  <option key={n} value={n}>
                    {n} portes
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Question 1 : kilométrage */}
      <div className="mc-field">
        <label className="mc-field-label" htmlFor="express-km">
          Kilométrage
        </label>
        <select
          id="express-km"
          className="mc-select"
          value={state.kilometrage}
          onChange={(e) => patch({ kilometrage: e.target.value })}
        >
          {KM_OPTIONS.map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
      </div>

      {/* Question 2 : boîte */}
      <div className="mc-field">
        <span className="mc-field-label">Boîte de vitesses</span>
        <div className="mc-choice-row">
          <Choice
            id="express-gb-0"
            name="express-gearbox"
            value="0"
            checked={state.vitesseType === '0'}
            onChange={(e) => patch({ vitesseType: e.target.value })}
          >
            Manuelle
          </Choice>
          <Choice
            id="express-gb-1"
            name="express-gearbox"
            value="1"
            checked={state.vitesseType === '1'}
            onChange={(e) => patch({ vitesseType: e.target.value })}
          >
            Automatique
          </Choice>
        </div>
      </div>

      {/* Question 3 : lieu */}
      <div className="mc-field">
        <SelectedEmplacementDeliveryInput handleSelectLocation={onPlace} resetInput={false} />
        {state.place && <p className="mc-field-hint">Remise du véhicule : {state.place.label}</p>}
      </div>

      {/* Équipements à confirmer */}
      <div className="mc-field">
        <span className="mc-field-label">Équipements disponibles</span>
        <p className="mc-field-hint">
          Cochez uniquement ce que votre voiture possède : l'annonce ne mentionnera rien d'autre.
        </p>
        <div className="mc-choice-row">
          {EXTRA_OPTIONS.map(({ id, label, icon }) => (
            <Choice
              key={id}
              type="checkbox"
              variant="mc-choice--card"
              id={`express-opt-${id}`}
              value={id}
              checked={state.options.includes(Number(id))}
              onChange={() => toggleOption(Number(id))}
            >
              <ion-icon name={icon}></ion-icon> {label}
              {state.detected.includes(Number(id)) && (
                <em className="mv-express-tag">détecté sur vos photos</em>
              )}
            </Choice>
          ))}
        </div>
      </div>

      <div className="mc-form-actions">
        <button type="button" className="mc-btn mc-btn--secondary" onClick={onBack} disabled={busy}>
          <ion-icon name="arrow-back-outline"></ion-icon>
          Précédent
        </button>
        <button
          type="button"
          className="mc-btn mc-btn--primary"
          onClick={onNext}
          disabled={!canContinue || busy}
        >
          {busy ? 'Préparation…' : 'Voir mon annonce'}
          <ion-icon name="arrow-forward-outline"></ion-icon>
        </button>
      </div>
    </section>
  );
}

export default StepQuestions;
